import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { publicApi, getImageUrl } from '../lib/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function GallerySlideshow() {
  const [images, setImages] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.getGallery().then(res => {
      setImages(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const next = useCallback(() => {
    if (images.length > 0) setCurrent(c => (c + 1) % images.length);
  }, [images.length]);

  const prev = useCallback(() => {
    if (images.length > 0) setCurrent(c => (c - 1 + images.length) % images.length);
  }, [images.length]);

  // Auto-advance
  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [images.length, next]);

  if (loading || images.length === 0) return null;

  return (
    <section className="py-16 sm:py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <span className="text-xs text-[#46C7FF] uppercase tracking-[0.2em] font-semibold">Galerie</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3" style={{ fontFamily: 'Space Grotesk' }}>Impressionen</h2>
        </div>
        <div className="relative rounded-2xl overflow-hidden bg-[hsl(0,0%,7%)] aspect-[21/9]">
          <AnimatePresence mode="wait">
            <motion.img
              key={images[current]._id}
              src={getImageUrl(`/api/public/gallery/${images[current]._id}/image`)}
              alt={images[current].title || 'Galerie'}
              className="w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
            />
          </AnimatePresence>
          {/* Title overlay */}
          {images[current].title && (
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 bg-gradient-to-t from-black/60 to-transparent">
              <p className="text-sm sm:text-base text-white/80 font-medium">{images[current].title}</p>
            </div>
          )}
          {/* Navigation */}
          {images.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white/70 hover:text-white hover:bg-black/60 transition-colors duration-200">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white/70 hover:text-white hover:bg-black/60 transition-colors duration-200">
                <ChevronRight className="w-5 h-5" />
              </button>
              {/* Dots */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.map((_, i) => (
                  <button key={i} onClick={() => setCurrent(i)}
                    className={`w-2 h-2 rounded-full transition-colors duration-200 ${i === current ? 'bg-white' : 'bg-white/30'}`} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
